import { Button } from '@mantine/core';

import CardButton from '@/components/PatientModal/CardButton.tsx';
import PatientRecordLink from '@/components/PatientRecordLink.tsx';
import { AppointmentPill } from '@/components/appointment-pill.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { CardStatus } from '@/models/CardStatus.ts';
import { GenericPatient } from '@/models/GenericPatient.ts';
import { TaskModalMode } from '@/models/TaskModalMode.ts';

import { getInsuranceColor } from '@/utils/insurance-utils.ts';
import { openAppointmentScheduler } from '@/utils/modal/appointmentSchedulerUtils.tsx';
import { getPatientInfoLine } from '@/utils/patientUtils.ts';
import { buildPatientTaskPrefill } from '@/utils/task-utils.ts';
import { openBookingTaskModal } from '@/utils/task-utils.tsx';

interface Props {
    patient: GenericPatient | undefined;
    pvsPatientId: string;
    instanceId: string;
    isCardReadEnabled: boolean;
    isTasksEnabled: boolean;
    insuranceCardReadStatus: CardStatus | undefined;
    onCardRead: () => void;
}

export const PatientModalHeader = ({
    patient,
    pvsPatientId,
    instanceId,
    isCardReadEnabled,
    insuranceCardReadStatus,
    onCardRead,
    isTasksEnabled,
}: Props) => {
    const handleBookAppointment = (): void => {
        if (!patient) return;

        openAppointmentScheduler({
            mode: 'book',
            patient: patient,
        });
    };

    const handleCreateTask = (): void => {
        if (!patient) return;

        openBookingTaskModal({
            mode: TaskModalMode.EDITOR,
            prefill: buildPatientTaskPrefill(patient),
        });
    };

    const { dateOfBirthWithAge, genderCode, costCarriers } = getPatientInfoLine(patient);

    return (
        <div className="flex flex-col gap-3 px-0.5">
            <div className="flex flex-wrap items-center gap-4">
                <div className="flex shrink-0 gap-1">
                    <Paragraph>PN:</Paragraph>
                    <Paragraph>{patient?.patient_number}</Paragraph>
                </div>
                {dateOfBirthWithAge && <Paragraph className="whitespace-nowrap">{dateOfBirthWithAge}</Paragraph>}
                {genderCode && <Paragraph className="whitespace-nowrap">{genderCode}</Paragraph>}
                {costCarriers.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1">
                        {costCarriers.map((carrier) => (
                            <AppointmentPill
                                key={carrier.insurance}
                                text={carrier.insurance}
                                color={getInsuranceColor(carrier.isCardActive, carrier.insurance)}
                            />
                        ))}
                    </div>
                )}
                <PatientRecordLink pvsPatientId={pvsPatientId} instanceId={instanceId} />
            </div>
            <div className="flex items-center justify-end gap-2">
                {isCardReadEnabled && insuranceCardReadStatus && (
                    <CardButton status={insuranceCardReadStatus} onClick={onCardRead} />
                )}
                {isTasksEnabled && (
                    <Button variant="outline" disabled={!patient} onClick={handleCreateTask}>
                        + Aufgabe
                    </Button>
                )}
                <Button variant="light" disabled={!patient} onClick={handleBookAppointment}>
                    + Termin
                </Button>
            </div>
        </div>
    );
};
